import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Scales, Gavel, Trophy, ArrowRight, Coins, ChartLineUp, Play,
  Warning, ShieldCheck, Fire, GraduationCap, Book, Buildings, Bank, Flag, Globe,
} from "@phosphor-icons/react";

/* ---------------------------------------------------------------------------
 * Legal Mastery Game — Phase B (Home / Round / Results / Leaderboard)
 * Route entry: /games/legal-championship
 * ------------------------------------------------------------------------- */

const SUBJECT_ICONS = {
  constitutional: Bank,
  contract: Book,
  criminal: ShieldCheck,
  tort: Warning,
  property: Buildings,
  corporate: Buildings,
  ip: GraduationCap,
  international: Globe,
  adr: Scales,
  legal_systems: Globe,
  legal_methods: Book,
  jurisprudence: GraduationCap,
  evidence: ShieldCheck,
  family: Book,
  labour: Book,
  administrative: Bank,
  tax: Bank,
  oil_gas: Warning,
  natural_resources: Globe,
  environmental: Globe,
  human_rights: Scales,
  banking: Bank,
};

const TIER_COLORS = {
  pupil:            "bg-slate-500/20 text-slate-200 border-slate-500/40",
  associate:        "bg-blue-500/20 text-blue-200 border-blue-500/40",
  partner:          "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-500/40",
  senior_advocate:  "bg-amber-500/20 text-amber-200 border-amber-500/40",
};

function TierBadge({ tier }) {
  const label = ({
    pupil: "Pupil", associate: "Associate", partner: "Partner", senior_advocate: "Senior Advocate",
  })[tier] || tier;
  return (
    <Badge className={`gap-1 border ${TIER_COLORS[tier] || TIER_COLORS.pupil}`}>
      <Gavel size={12} weight="fill" /> {label}
    </Badge>
  );
}

/* ============================ HOME ==================================== */
export default function LegalHome() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [meta, setMeta] = useState(null);
  const [gameDoc, setGameDoc] = useState(null);
  const [player, setPlayer] = useState(null);
  const [subject, setSubject] = useState("contract");
  const [mode, setMode] = useState("tournament");
  const [starting, setStarting] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [country, setCountry] = useState("Ghana");
  const [system, setSystem] = useState("common");
  const [activeSession, setActiveSession] = useState(null);
  const [season, setSeason] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [m, p, act, se, rec, g] = await Promise.all([
          api.get("/legal-game/meta"),
          api.get("/legal-game/player"),
          api.get("/legal-game/active-session").catch(() => ({ data: { active: false } })),
          api.get("/legal-game/season/current").catch(() => ({ data: { active: false } })),
          api.get("/legal-game/sessions/recent").catch(() => ({ data: [] })),
          api.get("/games/game_legal_championship").catch(() => ({ data: null })),
        ]);
        setMeta(m.data);
        setGameDoc(g.data);
        setPlayer(p.data);
        setCountry(p.data?.jurisdiction_country || "Ghana");
        setSystem(p.data?.jurisdiction_system || "common");
        setActiveSession(act.data?.active ? act.data : null);
        setSeason(se.data?.active ? se.data : null);
        setRecentSessions(rec.data || []);
      } catch (e) {
        toast.error("Failed to load game");
      }
    })();
  }, []);

  const savePrefs = async (nextCountry, nextSystem) => {
    setSavingPrefs(true);
    try {
      const r = await api.put("/legal-game/player", {
        jurisdiction_country: nextCountry, jurisdiction_system: nextSystem,
      });
      setPlayer(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save preferences");
    } finally { setSavingPrefs(false); }
  };

  const entryCost = meta?.tournament_entry_credits ?? 3;
  const iracCost = meta?.irac_unlock_credits ?? 5;
  const payout = meta?.payout || {};

  const start = async () => {
    // If a round is already in progress, always resume it — never silently start a new one.
    if (activeSession) {
      toast.info("Resuming your round in progress.");
      nav(`/games/legal-championship/play/${activeSession.session_id}`);
      return;
    }
    if (mode === "tournament" && (user?.points || 0) < entryCost) {
      toast.error(`Need ${entryCost} Credits to enter tournament. Try Chambers Practice instead.`);
      return;
    }
    setStarting(true);
    try {
      const r = await api.post("/legal-game/round/start", { subject, mode });
      sessionStorage.setItem(`legal-session-${r.data.session_id}`, JSON.stringify(r.data));
      refresh?.();
      nav(`/games/legal-championship/play/${r.data.session_id}`);
    } catch (e) {
      // 409 = active session on server; backend returns the active session_id.
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.active_session_id) {
        toast.info("Resuming your round in progress.");
        nav(`/games/legal-championship/play/${detail.active_session_id}`);
        return;
      }
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Could not start round"));
    } finally { setStarting(false); }
  };

  const tier = (player?.difficulty_by_subject || {})[subject] || "pupil";
  const totals = player?.totals || {};

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto space-y-6" data-testid="legal-home">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-widest font-bold">
          <Scales size={14} weight="fill" /> Legal Mastery
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-black leading-tight">
          {gameDoc?.name || "The Dwamena & Associates"}<br />
          <span className="text-emerald-400">Global Legal Championship</span>
        </h1>
        <p className="text-white/70 max-w-2xl">
          {gameDoc?.description ||
            "Adaptive multiple-choice tournament for tertiary law students. Answer 10 questions per round. Get 3 in a row correct — move up. Miss 2 — move down. Climb from Pupil to Senior Advocate."}
        </p>
      </header>

      {/* Session Rejoin Card — shown when a round is mid-play */}
      {activeSession && (
        <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/40" data-testid="legal-rejoin-card">
          <CardContent className="p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1">
                <Fire size={12} weight="fill" /> Round in progress
              </div>
              <div className="font-heading font-black text-lg mt-1">
                Resume {activeSession.mode === "tournament" ? "Championship" : "Practice"} — {activeSession.subject}
              </div>
              <div className="text-xs text-white/60 mt-0.5">
                {activeSession.answered} / {activeSession.total} answered · <span className="uppercase">{activeSession.difficulty}</span>
              </div>
            </div>
            <Button
              data-testid="legal-rejoin-btn"
              onClick={() => nav(`/games/legal-championship/play/${activeSession.session_id}`)}
              className="bg-amber-400 hover:bg-amber-500 text-black font-heading font-bold gap-1"
            >
              Resume round <ArrowRight size={16} weight="bold" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Moot Season Card */}
      {season && <SeasonCard season={season} nav={nav} />}

      {/* Player summary */}
      <Card className="bg-[#111] border-white/10">
        <CardContent className="p-4 md:p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-white/50 mb-1">Credits</div>
            <div className="text-2xl font-black text-yellow-300 flex items-center gap-2">
              <Coins size={22} weight="fill" /> {user?.points ?? 0}
            </div>
          </div>
          <div>
            <div className="text-xs text-white/50 mb-1">Rounds played</div>
            <div className="text-2xl font-black">{totals.rounds ?? 0}</div>
          </div>
          <div>
            <div className="text-xs text-white/50 mb-1">Correct answers</div>
            <div className="text-2xl font-black text-emerald-400">{totals.correct ?? 0}</div>
          </div>
          <div>
            <div className="text-xs text-white/50 mb-1">Best streak</div>
            <div className="text-2xl font-black flex items-center gap-1"><Fire size={20} weight="fill" className="text-orange-400"/> {player?.highest_streak ?? 0}</div>
          </div>
        </CardContent>
      </Card>

      {/* Jurisdiction */}
      <Card className="bg-[#111] border-white/10">
        <CardContent className="p-4 md:p-5 space-y-3">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2">
            <Flag size={18} weight="fill" className="text-cyan-400" /> Jurisdiction
          </h3>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50 mb-1 block">Country</label>
              <Select value={country} onValueChange={(v) => { setCountry(v); const found = meta?.jurisdictions?.find(j=>j.country===v); const sys = found?.system || system; setSystem(sys); savePrefs(v, sys); }}>
                <SelectTrigger data-testid="legal-country-select" className="bg-black/40 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white max-h-72">
                  {(meta?.jurisdictions || []).map(j => (
                    <SelectItem key={j.country} value={j.country}>{j.flag} {j.country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-white/50 mb-1 block">Legal system</label>
              <Select value={system} onValueChange={(v) => { setSystem(v); savePrefs(country, v); }}>
                <SelectTrigger data-testid="legal-system-select" className="bg-black/40 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                  <SelectItem value="common">Common Law</SelectItem>
                  <SelectItem value="civil">Civil Law</SelectItem>
                  <SelectItem value="mixed">Mixed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {savingPrefs && <div className="text-xs text-white/50">Saving…</div>}
        </CardContent>
      </Card>

      {/* Subject */}
      <Card className="bg-[#111] border-white/10">
        <CardContent className="p-4 md:p-5 space-y-3">
          <h3 className="font-heading font-bold text-lg">Choose Your Subject</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {(meta?.subjects || []).map(s => {
              const Icon = SUBJECT_ICONS[s.key] || Book;
              const sTier = (player?.difficulty_by_subject || {})[s.key] || "pupil";
              const active = subject === s.key;
              return (
                <button
                  key={s.key}
                  data-testid={`legal-subject-${s.key}`}
                  onClick={() => setSubject(s.key)}
                  className={`relative rounded-lg border p-3 text-left transition-all ${active ? "bg-emerald-500/15 border-emerald-500/60" : "bg-black/30 border-white/10 hover:border-white/25"}`}
                >
                  <Icon size={22} weight="fill" className={active ? "text-emerald-400" : "text-white/70"} />
                  <div className="text-sm font-semibold mt-1.5 leading-tight">{s.label}</div>
                  <div className="mt-2"><TierBadge tier={sTier} /></div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Mode picker + Play CTA */}
      <Card className="bg-[#0f0f0f] border-emerald-500/20">
        <CardContent className="p-4 md:p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">
            <button
              data-testid="legal-mode-tournament"
              onClick={() => setMode("tournament")}
              className={`rounded-lg border p-4 text-left ${mode === "tournament" ? "bg-emerald-500/15 border-emerald-500/60" : "bg-black/30 border-white/10"}`}
            >
              <div className="flex items-center gap-2"><Trophy size={20} weight="fill" className="text-amber-300"/><span className="font-heading font-bold">Championship Round</span></div>
              <p className="text-sm text-white/70 mt-1.5">
                <span data-testid="legal-entry-cost" className="font-black text-yellow-300">{entryCost} Credits</span> entry per round.
                Earn merit-based Credits + climb the global leaderboard.
              </p>
            </button>
            <button
              data-testid="legal-mode-practice"
              onClick={() => setMode("practice")}
              className={`rounded-lg border p-4 text-left ${mode === "practice" ? "bg-emerald-500/15 border-emerald-500/60" : "bg-black/30 border-white/10"}`}
            >
              <div className="flex items-center gap-2"><Book size={20} weight="fill" className="text-blue-300"/><span className="font-heading font-bold">Chambers Practice</span></div>
              <p className="text-sm text-white/70 mt-1.5">Free. No Credits deducted, no leaderboard change — pure learning.</p>
            </button>
          </div>

          {/* Admin-configured payout schedule */}
          {Object.keys(payout).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1" data-testid="legal-payout-schedule">
              {["pupil", "associate", "partner", "senior_advocate"].map(t => (
                <div key={t} className="bg-black/25 border border-white/10 rounded-md p-2 text-xs">
                  <div className="uppercase text-white/50 text-[10px]">{t.replace("_", " ")}</div>
                  <div className="font-black text-yellow-300 mt-0.5">
                    {payout[t]?.perfect ?? 0} cr <span className="text-white/50 font-normal">perfect</span>
                  </div>
                  <div className="text-[11px] text-white/60">
                    {payout[t]?.per_correct ?? 0} cr <span className="text-white/40">per correct</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="text-[11px] text-white/50" data-testid="legal-irac-cost">
            IRAC explainer costs <span className="text-yellow-300 font-black">{iracCost} Credits</span> per question.
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-2 border-t border-white/10">
            <div className="flex items-center gap-3">
              <span className="text-sm text-white/70">Your tier in {(meta?.subjects || []).find(s=>s.key===subject)?.label}:</span>
              <TierBadge tier={tier} />
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="legal-leaderboard-btn"
                variant="outline"
                className="border-white/20"
                onClick={() => nav("/games/legal-championship/leaderboard")}
              >
                <ChartLineUp size={16} weight="bold" className="mr-1"/> Leaderboard
              </Button>
              {season && (
                <Button
                  data-testid="legal-standings-btn"
                  variant="outline"
                  className="border-amber-500/40 text-amber-300"
                  onClick={() => nav("/games/legal-championship/standings")}
                >
                  <Trophy size={16} weight="fill" className="mr-1"/> Season standings
                </Button>
              )}
              <Button
                data-testid="legal-start-btn"
                disabled
                className="min-h-[44px] bg-white/10 text-white/70 font-heading font-bold gap-2 cursor-not-allowed"
              >
                Feature temporarily paused for maintenance
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Question Ledger — recent finished rounds with review CTA */}
      {recentSessions.length > 0 && (
        <Card className="bg-[#111] border-white/10" data-testid="legal-ledger-card">
          <CardContent className="p-4 md:p-5 space-y-3">
            <h3 className="font-heading font-bold text-lg flex items-center gap-2">
              <Book size={18} weight="fill" className="text-blue-300"/> Recent rounds — review the answer key
            </h3>
            <div className="grid gap-2">
              {recentSessions.slice(0, 5).map(s => (
                <button
                  key={s.session_id}
                  data-testid={`legal-ledger-row-${s.session_id}`}
                  onClick={() => nav(`/games/legal-championship/review/${s.session_id}`)}
                  className="flex items-center gap-3 p-2.5 rounded-lg bg-black/25 border border-white/10 hover:border-emerald-500/40 text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{s.subject} · <span className="text-white/50 uppercase text-xs">{s.difficulty}</span></div>
                    <div className="text-xs text-white/50">{new Date(s.finished_at).toLocaleDateString()} · {s.mode}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-emerald-400 font-black">{s.correct_count} / 10</div>
                    <div className="text-xs text-yellow-300 flex items-center gap-1 justify-end"><Coins size={11} weight="fill"/> {s.credits_earned}</div>
                  </div>
                  <ArrowRight size={14} weight="bold" className="text-white/40"/>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="text-xs text-white/40 text-center pt-2">
        Merit-based leaderboard. Credits cannot buy rank, tier promotion or round passes.
        {" "}<Link to="/buy-points" className="underline text-yellow-300">Need more Credits?</Link>
      </div>
    </div>
  );
}

/* ============================ SEASON CARD ============================ */
function fmtCountdown(secs) {
  if (secs == null || secs <= 0) return "—";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function SeasonCard({ season, nav }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);
  const cur = (season?.rounds || []).find(r => r.round_number === season?.current_round);
  const you = season?.you;
  const isEliminated = you?.status === "eliminated";
  const joined = !!you;
  const [joining, setJoining] = useState(false);

  const join = async () => {
    setJoining(true);
    try {
      await api.post("/legal-game/season/join");
      toast.success("Joined the Moot Season");
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to join");
    } finally { setJoining(false); }
  };

  return (
    <Card className="bg-gradient-to-br from-amber-500/10 via-black to-emerald-500/10 border-amber-500/30" data-testid="legal-season-card">
      <CardContent className="p-4 md:p-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1">
              <Trophy size={12} weight="fill"/> Moot Season
            </div>
            <h3 className="font-heading font-black text-xl mt-1">{season.name}</h3>
            <div className="text-xs text-white/60 mt-0.5">
              15-day tournament · 5 Rounds × 3 Days · elimination at every round
            </div>
          </div>
          {!joined && season.status !== "concluded" && (
            <Button
              data-testid="legal-season-join-btn"
              onClick={join}
              disabled={joining}
              className="bg-amber-400 hover:bg-amber-500 text-black font-heading font-bold"
            >{joining ? "Joining…" : "Join season"}</Button>
          )}
          {joined && (
            <Badge className={`gap-1 border ${isEliminated ? "bg-rose-500/15 text-rose-300 border-rose-500/40" : "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"}`}>
              {isEliminated ? `Eliminated · Round ${you.eliminated_round}` : "You're in"}
            </Badge>
          )}
        </div>

        {/* Round pips */}
        <div className="grid grid-cols-5 gap-1.5">
          {(season.rounds || []).map(r => {
            const active = r.state === "active";
            const closed = r.state === "closed";
            return (
              <div key={r.round_number} data-testid={`legal-season-round-${r.round_number}`}
                className={`p-2 rounded-md border text-center text-xs ${
                  active ? "bg-amber-500/25 border-amber-500/50 text-amber-100"
                  : closed ? "bg-white/5 border-white/10 text-white/50"
                  : "bg-black/30 border-white/10 text-white/60"
                }`}>
                <div className="font-black text-sm">R{r.round_number}</div>
                <div className="text-[10px] leading-tight mt-0.5">
                  {closed ? "Closed" : active ? "LIVE" : "Upcoming"}
                </div>
                <div className="text-[10px] text-white/50 mt-0.5">-{r.elimination_pct}%</div>
              </div>
            );
          })}
        </div>

        {cur && (
          <div className="flex items-center justify-between text-sm bg-black/30 rounded-md p-2.5 border border-amber-500/20">
            <div>
              <span className="text-white/60">Round {cur.round_number} closes in:</span>{" "}
              <span className="font-black text-amber-300" data-testid="legal-season-countdown">
                {fmtCountdown(cur.seconds_remaining - (tick * 30))}
              </span>
            </div>
            {joined && !isEliminated && (
              <div className="text-white/70">
                Your R{cur.round_number}:{" "}
                <span className="font-black text-emerald-400">
                  {(you?.correct_by_round || {})[cur.round_number] || (you?.correct_by_round || {})[String(cur.round_number)] || 0}
                </span> correct
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            className="border-amber-500/40 text-amber-300"
            onClick={() => nav("/games/legal-championship/standings")}
          >
            <ChartLineUp size={14} weight="bold" className="mr-1"/> Standings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}



/* ============================ ROUND ==================================== */
export function LegalRound() {
  const nav = useNavigate();
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});     // {qid: {selected, correct, correct_option, streak}}
  const [locked, setLocked] = useState(false);
  const [iracByQ, setIracByQ] = useState({});     // {qid: expl}
  const [iracCost, setIracCost] = useState(5);
  const [iracLoading, setIracLoading] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [reporting, setReporting] = useState({});

  useEffect(() => {
    // Fetch admin-configured IRAC cost so the button label matches reality.
    api.get("/legal-game/meta")
      .then(r => setIracCost(r.data?.irac_unlock_credits ?? 5))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const raw = sessionStorage.getItem(`legal-session-${sessionId}`);
      if (raw) {
        try { setSession(JSON.parse(raw)); return; } catch { /* fall through to fetch */ }
      }
      try {
        const r = await api.get(`/legal-game/session/${sessionId}`);
        if (cancelled) return;
        if (r.data.finished_at) {
          nav(`/games/legal-championship/results/${sessionId}`, { replace: true });
          return;
        }
        // Restore prior answers when rehydrating mid-round.
        const restored = {};
        for (const a of (r.data.answers || [])) {
          restored[a.question_id] = {
            selected: a.selected,
            correct: a.correct,
            correct_option: a.correct ? a.selected : null,
          };
        }
        setAnswers(restored);
        setSession(r.data);
      } catch {
        toast.error("Session expired — please start a new round.");
        nav("/games/legal-championship");
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, nav]);

  const questions = session?.questions || [];
  const q = questions[idx];
  const answer = q ? answers[q.question_id] : null;
  const total = questions.length;
  const answeredCount = Object.keys(answers).length;

  const submitAnswer = async (selected) => {
    if (!q || answer || locked) return;
    setLocked(true);
    try {
      const r = await api.post("/legal-game/round/answer", {
        session_id: sessionId, question_id: q.question_id, selected,
      });
      setAnswers(prev => ({ ...prev, [q.question_id]: { selected, ...r.data } }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally { setLocked(false); }
  };

  const unlockIrac = async () => {
    if (!q || iracByQ[q.question_id]) return;
    setIracLoading(true);
    try {
      const r = await api.post("/legal-game/irac/unlock", { question_id: q.question_id });
      setIracByQ(prev => ({ ...prev, [q.question_id]: r.data.irac_explanation }));
      if (r.data.cost_credits > 0) toast.success(`-${r.data.cost_credits} Credits`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to unlock");
    } finally { setIracLoading(false); }
  };

  const report = async () => {
    if (!q || reporting[q.question_id]) return;
    setReporting(p => ({ ...p, [q.question_id]: true }));
    try {
      await api.post("/legal-game/question/report", { question_id: q.question_id, reason: "user-flag" });
      toast.success("Reported. Thanks — admins will review.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to report");
    }
  };

  const finish = useCallback(async () => {
    setFinishing(true);
    try {
      const r = await api.post("/legal-game/round/finish", { session_id: sessionId });
      sessionStorage.removeItem(`legal-session-${sessionId}`);
      sessionStorage.setItem(`legal-result-${sessionId}`, JSON.stringify(r.data));
      nav(`/games/legal-championship/results/${sessionId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to finish");
    } finally { setFinishing(false); }
  }, [sessionId, nav]);

  if (!session || !q) {
    return <div className="p-8 text-center text-white/60">Loading round…</div>;
  }

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto space-y-4" data-testid="legal-round">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase text-emerald-400 tracking-widest font-bold">
            {(session.mode === "practice") ? "Chambers Practice" : "Championship Round"}
          </div>
          <div className="text-white/70 text-sm mt-0.5">
            {session.subject} · <span className="uppercase text-xs">{session.difficulty}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-white/50">Question</div>
          <div className="text-xl font-black">{idx + 1} / {total}</div>
        </div>
      </header>

      <Progress value={((idx + (answer ? 1 : 0)) / total) * 100} className="h-1.5 bg-white/10" />

      <Card className="bg-[#111] border-white/10">
        <CardContent className="p-5 space-y-4">
          <div data-testid="legal-question-text" className="text-lg leading-relaxed whitespace-pre-line">
            {q.question_text}
          </div>
          <div className="grid gap-2">
            {q.options.map((opt, i) => {
              const isSelected = answer?.selected === i;
              const isCorrect = answer && answer.correct_option === i;
              const isWrongPick = answer && isSelected && !answer.correct;
              const cls = !answer
                ? "bg-black/30 border-white/10 hover:border-white/30"
                : isCorrect
                  ? "bg-emerald-500/15 border-emerald-500/60"
                  : isWrongPick
                    ? "bg-rose-500/15 border-rose-500/60"
                    : "bg-black/20 border-white/10 opacity-70";
              return (
                <button
                  key={i}
                  data-testid={`legal-option-${i}`}
                  disabled={!!answer || locked}
                  onClick={() => submitAnswer(i)}
                  className={`w-full text-left p-3 rounded-lg border transition ${cls}`}
                >
                  <span className="text-white/60 mr-2 font-mono text-sm">{String.fromCharCode(65 + i)}.</span>
                  {opt}
                </button>
              );
            })}
          </div>

          {answer && (
            <div className="pt-2 border-t border-white/10 space-y-3">
              <div className={`text-sm font-semibold ${answer.correct ? "text-emerald-400" : "text-rose-400"}`}>
                {answer.correct ? "Correct." : "Incorrect."}
                {answer.new_difficulty && (
                  <span className="ml-2 text-white/50 font-normal">
                    Next tier: <TierBadge tier={answer.new_difficulty} />
                  </span>
                )}
              </div>

              {iracByQ[q.question_id] ? (
                <div data-testid="legal-irac-panel" className="bg-black/30 border border-emerald-500/30 rounded-lg p-3 text-sm text-white/85 whitespace-pre-line">
                  <div className="text-emerald-400 font-bold mb-1 text-xs uppercase tracking-widest">IRAC Analysis</div>
                  {iracByQ[q.question_id]}
                </div>
              ) : (
                <Button
                  data-testid="legal-irac-unlock"
                  onClick={unlockIrac}
                  disabled={iracLoading}
                  variant="outline"
                  className="border-emerald-500/40 text-emerald-300 gap-1"
                >
                  <Coins size={14} weight="fill" /> Unlock IRAC ({iracCost} Credits)
                </Button>
              )}

              <Button
                data-testid="legal-question-report"
                onClick={report}
                disabled={reporting[q.question_id]}
                variant="outline"
                size="sm"
                className="border-white/20 text-white/70 gap-1"
              >
                <Flag size={14} weight="fill" /> {reporting[q.question_id] ? "Reported" : "Report question"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-2 justify-between">
        <Button
          data-testid="legal-prev"
          variant="outline"
          className="border-white/20"
          onClick={() => setIdx(i => Math.max(0, i - 1))}
          disabled={idx === 0}
        >Prev</Button>

        {idx < total - 1 ? (
          <Button
            data-testid="legal-next"
            onClick={() => setIdx(i => Math.min(total - 1, i + 1))}
            disabled={!answer}
            className="bg-white/10 hover:bg-white/15"
          >Next question <ArrowRight size={14} weight="bold" className="ml-1"/></Button>
        ) : (
          <Button
            data-testid="legal-finish"
            onClick={finish}
            disabled={finishing || answeredCount === 0}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-heading font-bold"
          >{finishing ? "Finishing…" : "Finish round"}</Button>
        )}
      </div>
    </div>
  );
}

/* ============================ RESULTS ==================================== */
const BADGE_LABELS = {
  first_win: "First Bench Win",
  common_law_scholar: "Common Law Scholar",
  civil_law_scholar: "Civil Law Scholar",
  iron_streak: "Iron Streak",
  senior_advocate: "Senior Advocate",
};

export function LegalResults() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const raw = sessionStorage.getItem(`legal-result-${sessionId}`);
      if (raw) { try { setResult(JSON.parse(raw)); return; } catch { /* fall through */ } }
      try {
        const r = await api.get(`/legal-game/session/${sessionId}`);
        if (cancelled) return;
        if (!r.data.finished_at) {
          nav(`/games/legal-championship/play/${sessionId}`, { replace: true });
          return;
        }
        setResult({
          session_id: r.data.session_id,
          mode: r.data.mode,
          subject: r.data.subject,
          difficulty: r.data.difficulty,
          correct: r.data.correct_count,
          total: r.data.total,
          credits_earned: r.data.credits_earned,
          new_badges: [],
          perfect: r.data.perfect,
        });
      } catch (e) {
        if (!cancelled) setError("Session not found.");
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, nav]);

  if (error) {
    return (
      <div className="p-8 text-center space-y-4">
        <div className="text-white/60">{error}</div>
        <Button onClick={() => nav("/games/legal-championship")} className="bg-emerald-500 text-black">Back to Legal Home</Button>
      </div>
    );
  }
  if (!result) return <div className="p-8 text-center text-white/60">Loading result…</div>;
  const perfect = result.perfect;
  return (
    <div className="p-6 max-w-2xl mx-auto space-y-5" data-testid="legal-results">
      <div className="text-center space-y-2">
        <Trophy size={64} weight="fill" className={`mx-auto ${perfect ? "text-amber-300" : "text-white/40"}`} />
        <h1 className="font-heading text-3xl font-black">{perfect ? "Perfect round!" : "Round complete"}</h1>
        <p className="text-white/60">
          {result.correct} / {result.total} correct · {result.subject} · {result.difficulty}
        </p>
      </div>

      <Card className="bg-[#111] border-white/10">
        <CardContent className="p-5 grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-white/50">Credits earned</div>
            <div className="text-3xl font-black text-yellow-300 flex items-center gap-2">
              <Coins size={24} weight="fill" /> {result.credits_earned}
            </div>
          </div>
          <div>
            <div className="text-xs text-white/50">Mode</div>
            <div className="text-lg font-bold uppercase">{result.mode}</div>
          </div>
        </CardContent>
      </Card>

      {(result.new_badges || []).length > 0 && (
        <Card className="bg-amber-500/10 border-amber-500/40">
          <CardContent className="p-5">
            <div className="text-xs text-amber-300 uppercase tracking-widest font-bold mb-1">New badges unlocked</div>
            <div className="flex flex-wrap gap-2">
              {result.new_badges.map(b => (
                <Badge key={b} className="bg-amber-500/25 text-amber-200 border-amber-500/50">
                  {BADGE_LABELS[b] || b}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2 flex-col sm:flex-row">
        <Button
          data-testid="legal-play-again"
          onClick={() => nav("/games/legal-championship")}
          className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-black font-bold"
        >Play another round</Button>
        <Button
          data-testid="legal-view-leaderboard"
          variant="outline"
          className="flex-1 border-white/20"
          onClick={() => nav("/games/legal-championship/leaderboard")}
        >View leaderboard</Button>
      </div>
    </div>
  );
}

/* ============================ LEADERBOARD ==================================== */
export function LegalLeaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/legal-game/leaderboard");
        setRows(r.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);
  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto space-y-4" data-testid="legal-leaderboard">
      <h1 className="font-heading text-3xl font-black flex items-center gap-2">
        <Trophy size={26} weight="fill" className="text-amber-300"/> Legal Championship Leaderboard
      </h1>
      <p className="text-white/60 text-sm">Ranked by total correct answers, then longest streak. 100% merit-based.</p>
      {loading && <div className="text-white/50 text-sm">Loading…</div>}
      {!loading && rows.length === 0 && <div className="text-white/50 text-sm">No players yet. Be the first!</div>}
      <div className="space-y-1.5">
        {rows.map(r => (
          <Card key={r.user_id} className="bg-[#111] border-white/10">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 text-center font-black text-lg text-white/80">#{r.rank}</div>
              {r.picture ? (
                <img src={r.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-sm">{(r.name || "?")[0]}</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{r.name}</div>
                <div className="text-xs text-white/50 flex items-center gap-1"><Flag size={10}/> {r.country}</div>
              </div>
              <TierBadge tier={r.highest_tier} />
              <div className="text-right">
                <div className="text-xs text-white/50">Correct</div>
                <div className="font-black">{r.correct}</div>
              </div>
              <div className="text-right hidden sm:block">
                <div className="text-xs text-white/50">Streak</div>
                <div className="font-black flex items-center gap-1"><Fire size={12} weight="fill" className="text-orange-400"/>{r.highest_streak}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ============================ STANDINGS ==================================== */
export function LegalStandings() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/legal-game/season/standings");
        setData(r.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-8 text-center text-white/60">Loading standings…</div>;
  if (!data?.active) return (
    <div className="p-8 text-center space-y-3">
      <div className="text-white/60">No active Moot Season right now.</div>
      <Button onClick={() => nav("/games/legal-championship")} className="bg-emerald-500 text-black">Back to Legal Home</Button>
    </div>
  );

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto space-y-4" data-testid="legal-standings">
      <h1 className="font-heading text-3xl font-black flex items-center gap-2">
        <Trophy size={26} weight="fill" className="text-amber-300"/> {data.name} — Standings
      </h1>
      <p className="text-white/60 text-sm">
        {data.current_round ? `Round ${data.current_round} live · ` : ""}
        {data.threshold_score != null
          ? <span className="text-rose-300">Elimination line at <span className="font-black">{data.threshold_score}</span> correct — players at or below get cut when this round closes.</span>
          : "Threshold line will appear once elimination pressure kicks in."}
      </p>
      <div className="space-y-1.5">
        {data.rows.map((r, idx) => {
          const showLine = data.threshold_score != null
            && idx > 0
            && !r.below_threshold
            && data.rows[idx - 1]?.below_threshold;
          return (
            <>
              {showLine && (
                <div key={`line-${idx}`} data-testid="legal-elimination-line" className="flex items-center gap-2 py-1">
                  <div className="flex-1 h-px bg-rose-500/50"></div>
                  <div className="text-[10px] uppercase tracking-widest font-black text-rose-300">Elimination Line</div>
                  <div className="flex-1 h-px bg-rose-500/50"></div>
                </div>
              )}
              <Card key={r.user_id}
                className={`bg-[#111] border ${r.status === "eliminated" ? "border-rose-500/40 opacity-60"
                  : r.below_threshold ? "border-amber-500/40"
                  : "border-white/10"}`}
              >
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="w-10 text-center font-black text-lg text-white/80">#{r.rank}</div>
                  {r.picture ? (
                    <img src={r.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-sm">{(r.name || "?")[0]}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{r.name}</div>
                    <div className="text-xs text-white/50 flex items-center gap-1.5">
                      {r.status === "eliminated" && <Badge className="bg-rose-500/20 text-rose-200 border-rose-500/40 text-[10px] px-1.5 py-0">OUT · R{r.eliminated_round}</Badge>}
                      {r.status === "champion" && <Badge className="bg-amber-400 text-black text-[10px] px-1.5 py-0">CHAMPION</Badge>}
                      {r.below_threshold && r.status !== "eliminated" && <Badge className="bg-amber-500/20 text-amber-200 border-amber-500/40 text-[10px] px-1.5 py-0">At risk</Badge>}
                      {(r.badges || []).length > 0 && <span>{r.badges.length} badge{r.badges.length===1?"":"s"}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-white/50">Total</div>
                    <div className="font-black text-emerald-400">{r.total_correct}</div>
                  </div>
                </CardContent>
              </Card>
            </>
          );
        })}
      </div>
      <Button variant="outline" className="border-white/20" onClick={() => nav("/games/legal-championship")}>Back</Button>
    </div>
  );
}

/* ============================ REVIEW / QUESTION LEDGER =================== */
export function LegalReview() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/legal-game/session/${sessionId}/review`);
        setData(r.data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Session not found");
      }
    })();
  }, [sessionId]);
  if (err) return (
    <div className="p-8 text-center space-y-3">
      <div className="text-white/60">{err}</div>
      <Button onClick={() => nav("/games/legal-championship")} className="bg-emerald-500 text-black">Back to Legal Home</Button>
    </div>
  );
  if (!data) return <div className="p-8 text-center text-white/60">Loading review…</div>;
  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto space-y-4" data-testid="legal-review">
      <header>
        <div className="text-xs uppercase tracking-widest text-emerald-400 font-bold">Question Ledger</div>
        <h1 className="font-heading text-2xl md:text-3xl font-black">{data.subject} · {data.difficulty}</h1>
        <div className="text-sm text-white/60 mt-1">
          {new Date(data.finished_at).toLocaleString()} · {data.correct_count} / {data.total} correct · {data.credits_earned} Credits earned
        </div>
      </header>
      <div className="space-y-3">
        {data.questions.map((q, i) => (
          <Card key={q.question_id} className={`border ${q.correct ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/30 bg-rose-500/5"}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Badge className={q.correct ? "bg-emerald-500/25 text-emerald-200 border-emerald-500/50" : "bg-rose-500/25 text-rose-200 border-rose-500/50"}>
                  Q{i + 1} · {q.correct ? "Correct" : "Incorrect"}
                </Badge>
                {q.irac_previously_unlocked && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">IRAC unlocked</Badge>}
              </div>
              <div className="text-sm whitespace-pre-line">{q.question_text}</div>
              <ul className="text-xs space-y-1">
                {q.options.map((o, j) => {
                  const isCorrect = j === q.correct_option;
                  const isYours = j === q.selected;
                  return (
                    <li key={j} className={
                      isCorrect ? "text-emerald-300 font-semibold"
                      : isYours ? "text-rose-300"
                      : "text-white/60"
                    }>
                      <span className="font-mono mr-1.5">{String.fromCharCode(65 + j)}.</span>
                      {o}
                      {isCorrect && <span className="ml-1.5 text-[10px] uppercase">correct</span>}
                      {isYours && !isCorrect && <span className="ml-1.5 text-[10px] uppercase">your pick</span>}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-2 pt-2 border-t border-white/10 text-xs text-white/80 bg-black/30 rounded p-2 whitespace-pre-line">
                <div className="text-emerald-400 font-bold mb-1 text-[10px] uppercase tracking-widest">IRAC Analysis</div>
                {q.irac_explanation}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="flex gap-2">
        <Button onClick={() => nav("/games/legal-championship")} className="bg-emerald-500 text-black">Back to Legal Home</Button>
      </div>
    </div>
  );
}

